import { assert } from '@ember/debug';
import { isArray } from '@ember/array';
import { isPresent } from '@ember/utils';
import { baseConnectUrl } from '@square/dashboard-utilities/utils/url';
import { getCsrfToken, jsonPost, jsonGet, jsonDelete, jsonPut } from '@square/dashboard-utilities/utils/xhr';
import { camelize } from '@square/dashboard-utilities/utils/keys';
import snakeize from 'snakeize';

/**
 * This file is modified from frontend/dashboard/app/clients/catalog-connect-v2.js.
 * New Connect V2 clients can be created with this shared code.
 */

// replaces placeholders in URL template with attributes from passed request object
// i.e. createRequestURLFromTemplate('/object/{objectId}', { objectId: 'EXAMPLETOKEN'}) => '/object/EXAMPLETOKEN'
const URL_ATTRIBUTE_REGEX = /{(.*)}/g;
function createRequestURLFromTemplate(templateURL, request) {
  return templateURL.replace(URL_ATTRIBUTE_REGEX, (match, attrName) => {
    assert(`missing attribute value ${attrName} required by endpoint with url template ${templateURL}`, isPresent(request[attrName]));
    return request[attrName];
  });
}
function postFn({
  url,
  requestClass,
  responseClass,
  shouldCamelize,
  customJSONSerializer
}) {
  return function (options = {}, params = {}) {
    const req = requestClass.create(options);
    const formattedUrl = createRequestURLFromTemplate(url, req);

    // Add CSRF header to request params
    // eslint-disable-next-line no-param-reassign
    params['headers'] ||= {};
    // eslint-disable-next-line no-param-reassign
    params['headers']['X-CSRF-Token'] = getCsrfToken();
    return jsonPost(formattedUrl, customJSONSerializer ? customJSONSerializer(req) : snakeize(req.toJSON()), params).then(resp => {
      if (shouldCamelize) {
        return responseClass.fromObject(camelize(resp));
      } else {
        return resp;
      }
    });
  };
}
function getFn({
  url,
  requestClass,
  responseClass,
  shouldCamelize,
  customJSONSerializer
}) {
  return function (options = {}, ajaxOptions = {}) {
    const req = requestClass.create(options);
    let reqURL = createRequestURLFromTemplate(url, req);
    if (options && Object.keys(options).length > 0) {
      const json = customJSONSerializer ? customJSONSerializer(req) : snakeize(req.toJSON());
      const params = Object.keys(json).reduce((params, key) => {
        const value = json[key];
        if (isPresent(value)) {
          const paramValue = isArray(value) ? value.join(',') : value;
          params.push(`${key}=${paramValue}`);
        }
        return params;
      }, []);
      reqURL = `${reqURL}?${params.join('&')}`;
    }
    return jsonGet(reqURL, ajaxOptions).then(resp => {
      if (shouldCamelize) {
        return responseClass.fromObject(camelize(resp));
      } else {
        return resp;
      }
    });
  };
}
function putFn({
  url,
  requestClass,
  responseClass,
  shouldCamelize,
  customJSONSerializer
}) {
  return function (options = {}, params = {}) {
    const req = requestClass.create(options);

    // Add CSRF header to request params
    // eslint-disable-next-line no-param-reassign
    params['headers'] ||= {};
    // eslint-disable-next-line no-param-reassign
    params['headers']['X-CSRF-Token'] = getCsrfToken();
    return jsonPut(createRequestURLFromTemplate(url, req), customJSONSerializer ? customJSONSerializer(req) : snakeize(req.toJSON()), params).then(resp => shouldCamelize ? responseClass.fromObject(camelize(resp)) : resp);
  };
}
function deleteFn({
  url,
  requestClass,
  responseClass,
  shouldCamelize
}) {
  return function (options = {}, ajaxOptions = {}) {
    const req = requestClass.create(options);

    // eslint-disable-next-line no-param-reassign
    ajaxOptions['headers'] ||= {};
    // eslint-disable-next-line no-param-reassign
    ajaxOptions['headers']['X-CSRF-Token'] = getCsrfToken();
    return jsonDelete(createRequestURLFromTemplate(url, req), null, ajaxOptions).then(resp => shouldCamelize ? responseClass.fromObject(camelize(resp)) : resp);
  };
}
const methodsToFunc = {
  POST: postFn,
  GET: getFn,
  DELETE: deleteFn,
  PUT: putFn
};

/**
 * Creates a Connect V2 client based on endpointDefinitions and url.
 *
 * @param {Object} endpointDefinitions - a hash of endpointDefinitions
 * @param {Object} endpointDefinition.request - model of the request
 * @param {Object} endpointDefinition.response - model of the response
 * @param {string} endpointDefinition.path - request path, such as /search
 * @param {string} endpointDefinition.method - HTTP request method, such as POST, GET, DELETE, or PUT
 * @param {string} endpointDefinition.shouldCamelize - whether or not the returned data should be camelized
 * @param {Function} [endpointDefinition.customJSONSerializer(request: Object): Object] - optional custom json
 * // serializer if clients want to diverge from the default implementation of calling toJSON on the request model
 * // and calling snakeize on it. Function takes in a request model instance and returns a javascript object.
 * @param {string} urlPrefix - request url prefix, such as /v2/discount-codes
 * @returns {Object} A client that makes requests to defined endpoints by calling client.endpointDefinitionKey(...)
 */
export function createConnectV2Client(endpointDefinitions, urlPrefix, useSameHost = false) {
  return Object.keys(endpointDefinitions).reduce((client, key) => {
    const def = endpointDefinitions[key];
    const url = baseConnectUrl(`${urlPrefix}${def.path}`, useSameHost);
    // eslint-disable-next-line no-param-reassign
    client[key] = methodsToFunc[def.method]({
      url,
      requestClass: def.request,
      responseClass: def.response,
      shouldCamelize: def.shouldCamelize ?? true,
      customJSONSerializer: def.customJSONSerializer
    });
    // eslint-disable-next-line no-param-reassign
    client[key].url = url;
    // eslint-disable-next-line no-param-reassign
    client[key].method = def.method;
    return client;
  }, {});
}